import type { QueryClient } from '@tanstack/react-query';

import { getAdminShellPendingSummaries } from '../services/admin/shell';
import { getMyCustomers } from '../services/registrations/customers';
import { isCustomerBillingProfile } from '../utils/customerBillingProfile';
import type { IUser } from '../services/users/types';
import { queryKeys } from './queryKeys';
import {
  STALE_TIME_ADMIN_PENDING_MS,
  STALE_TIME_CUSTOMERS_ME_MS,
  STALE_TIME_NOTIFICATION_COUNT_MS,
} from './staleTimes';
import {
  fetchUnreadNotificationCount,
  useNotificationUnreadCountQuery,
} from './notificationsQueries';

const prefetchedShellSessions = new Set<string>();

function shellSessionKey(userId: string, isAdminShell: boolean, isCustomerShell: boolean): string {
  return `${userId}:${isAdminShell ? 'admin' : isCustomerShell ? 'customer' : 'other'}`;
}

export function resetShellQueryPrefetchCache(): void {
  prefetchedShellSessions.clear();
}

function hydrateAdminPendingSummariesCache(
  queryClient: QueryClient,
  summaries: Awaited<ReturnType<typeof getAdminShellPendingSummaries>>
): void {
  queryClient.setQueryData(queryKeys.admin.pendingProposals, summaries.marketIndicatorProposals);
  queryClient.setQueryData(
    queryKeys.admin.pendingMyStationFlags(undefined),
    summaries.myStationFlags
  );
}

/**
 * Prefetch único por sessão/usuário — evita duplicatas no hard refresh (incl. StrictMode).
 */
export function ensureShellQueriesPrefetched(
  queryClient: QueryClient,
  user: IUser,
  isAdminShell: boolean
): void {
  const isCustomerShell = isCustomerBillingProfile(user);
  const sessionKey = shellSessionKey(user.id, isAdminShell, isCustomerShell);
  if (prefetchedShellSessions.has(sessionKey)) {
    return;
  }
  prefetchedShellSessions.add(sessionKey);

  const prefetches: Array<Promise<unknown>> = [
    queryClient.prefetchQuery({
      queryKey: queryKeys.notifications.unreadCount(undefined),
      queryFn: () => fetchUnreadNotificationCount(),
      staleTime: STALE_TIME_NOTIFICATION_COUNT_MS,
    }),
  ];

  if (isCustomerShell) {
    prefetches.push(
      queryClient.prefetchQuery({
        queryKey: queryKeys.customers.me,
        queryFn: getMyCustomers,
        staleTime: STALE_TIME_CUSTOMERS_ME_MS,
      })
    );
  }

  if (isAdminShell) {
    prefetches.push(
      queryClient
        .fetchQuery({
          queryKey: queryKeys.admin.pendingSummaries,
          queryFn: getAdminShellPendingSummaries,
          staleTime: STALE_TIME_ADMIN_PENDING_MS,
        })
        .then((summaries) => {
          hydrateAdminPendingSummariesCache(queryClient, summaries);
        })
    );
  }

  void Promise.all(prefetches);
}

export { useNotificationUnreadCountQuery };
